import { useContext } from 'react';
import { CalculationContext } from './calculationContext';
import { formatPt } from './numberFormat';

function Osmometer() {
  const { calculation: result } = useContext(CalculationContext);
  const calculated = result?.osm ?? null;
  const measured = result?.measured ?? null;
  const ethanolContribution = result?.ethanolContribution ?? 0;
  const values = [calculated, measured, calculated === null ? null : calculated + 10]
    .filter((value) => value !== null);
  const lowest = values.length ? Math.min(...values) : 270;
  const highest = values.length ? Math.max(...values) : 290;
  const minimum = Math.floor((lowest - 25) / 10) * 10;
  const maximum = Math.ceil((highest + 25) / 10) * 10;
  const position = (value) => `${((value - minimum) / (maximum - minimum)) * 100}%`;
  const ticks = Array.from({ length: 5 }, (_, index) => minimum + ((maximum - minimum) * index / 4));
  const baseValue = calculated === null ? null : calculated - ethanolContribution;

  return (
    <aside className="osmometer" aria-label="Visualização da osmolaridade estimada e medida">
      <div className="osmometer-topline">
        <span>Escala de osmolaridade</span>
        <span>mOsm/L · mOsm/kg</span>
      </div>
      <div className="osmometer-reading">
        <div>
          <span>Calculada</span>
          <strong>{calculated === null ? '—' : formatPt(calculated)}</strong>
        </div>
        <div className="reading-measured">
          <span>Medida</span>
          <strong>{measured === null ? '—' : formatPt(measured)}</strong>
        </div>
      </div>
      <div className="scale-wrap">
        <div className="scale-track">
          {calculated !== null && (
            <span
              className="reference-band"
              style={{ left: position(calculated), width: `${(10 / (maximum - minimum)) * 100}%` }}
            />
          )}
          {calculated !== null && ethanolContribution > 0 && (
            <span
              className="ethanol-segment"
              style={{
                left: position(baseValue),
                width: `${(ethanolContribution / (maximum - minimum)) * 100}%`,
              }}
            />
          )}
          {calculated !== null && (
            <span className="scale-marker calculated-marker" style={{ left: position(calculated) }} />
          )}
          {measured !== null && (
            <span className="scale-marker measured-marker" style={{ left: position(measured) }} />
          )}
        </div>
        <div className="scale-ticks">
          {ticks.map((tick) => <span key={tick}>{formatPt(tick, 0)}</span>)}
        </div>
      </div>
      <div className="scale-legend">
        <span><i className="legend-dot calculated-dot" /> Calculada</span>
        <span><i className="legend-dot measured-dot" /> Medida</span>
        <span><i className="legend-swatch" /> Até 10 de gap*</span>
      </div>
      <div className="gap-bracket">
        {calculated !== null && measured !== null ? (
          <>
            <span className="bracket-line" aria-hidden="true" />
            <p>
              Gap osmolar
              <strong>
                {formatPt(measured - calculated)} <small>mOsm/kg</small>
              </strong>
            </p>
          </>
        ) : (
          <p className="bracket-hint">Informe a osmolalidade medida para visualizar o gap.</p>
        )}
      </div>
      {calculated !== null && ethanolContribution > 0 && (
        <p className="ethanol-note">
          O trecho adicional representa o etanol: +{formatPt(ethanolContribution)} mOsm/L.
        </p>
      )}
      <p className="scale-footnote">
        * Referência citada para o gap; não é uma faixa de referência da osmolaridade.
      </p>
    </aside>
  );
}

export default Osmometer;
